import { Head, Link } from '@inertiajs/react'
import {
  Snowflake,
  ShoppingBasket,
  BookOpen,
  CalendarDays,
  Link2,
  Users,
  Mic,
  Bell,
  Package,
  Check,
  ArrowRight,
} from 'lucide-react'
import { Wordmark } from '@/Components/Shared/Wordmark'

const LOOP_STEPS = [
  {
    icon: ShoppingBasket,
    title: 'Stock up',
    body: 'Whether it\'s a great deal or your weekly grocery run, stock up and keep track — you\'ll always know what you bought and remember to use it.',
  },
  {
    icon: Package,
    title: 'Add it to your freezer',
    body: 'Log it in seconds: quantity, date added, and which freezer it\'s in.',
  },
  {
    icon: BookOpen,
    title: 'My Recipes',
    body: 'Import any recipe from a URL or add your own, then track what you cook — complete with notes and how often you make it.',
  },
  {
    icon: CalendarDays,
    title: 'Meal Plan',
    body: 'Plan your week with confidence — get meal ideas based on what\'s already in your freezer, and watch ingredients clear automatically as you cook.',
  },
]

const FEATURES = [
  { icon: Snowflake, title: 'Multi-freezer tracking', body: 'Kitchen, garage, chest freezer — track them all in one place, with item age at a glance.' },
  { icon: Link2, title: 'Recipe import from any URL', body: 'Paste a link and AI pulls in the ingredients and steps automatically.' },
  { icon: CalendarDays, title: 'Smart meal planning', body: 'Plan meals for the week and clear used ingredients from inventory when you cook.' },
  { icon: Users, title: 'Household Sharing', body: 'One inventory, everyone in the house — no more guessing or asking if something\'s already in the freezer.' },
  { icon: Mic, title: 'Alexa voice control', body: 'Add, check, or update your inventory hands-free while you cook.' },
  { icon: Bell, title: 'Stay Fresh', body: 'Set custom alerts for any timeframe you choose — get a nudge before something gets forgotten in the back of the freezer.' },
]

function formatPrice(amount, interval) {
  if (!amount) return null
  const dollars = (amount / 100).toFixed(2)
  const perLabel = interval === 'year' ? 'yr' : 'mo'
  return `$${dollars}/${perLabel}`
}

export default function Welcome({ prices }) {
  const monthlyPrice = formatPrice(prices?.monthly?.amount, prices?.monthly?.interval)
  const annualPrice = formatPrice(prices?.annual?.amount, prices?.annual?.interval)

  return (
    <>
      <Head title="Freeezer — Household Freezer & Recipe Manager">
        <meta
          name="description"
          content="Track every freezer in your house, import recipes from any URL, plan meals ahead, and share it all with your household. Free to start."
        />
      </Head>

      <div className="min-h-screen bg-white dark:bg-slate-950 font-['Inter',sans-serif]">
        {/* Header */}
        <header className="max-w-6xl mx-auto flex items-center justify-between px-6 py-6">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-cyan-500">
              <Snowflake className="w-4 h-4 text-white" />
            </div>
            <Wordmark className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight" />
          </div>
          <nav className="flex items-center gap-3">
            <Link
              href={route('login')}
              className="px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
            >
              Log in
            </Link>
            <Link
              href={route('register')}
              className="px-4 py-2 text-sm font-medium bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors"
            >
              Get Started Free
            </Link>
          </nav>
        </header>

        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 pt-10 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm font-semibold text-cyan-600 dark:text-cyan-400 mb-3">
              Household Freezer &amp; Recipe Manager
            </p>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white leading-tight mb-5">
              Never wonder what's in your freezer again.
            </h1>
            <p className="text-lg text-slate-500 dark:text-slate-400 leading-relaxed mb-8">
              Track every freezer in your house, save and import recipes from any site, and plan
              meals ahead — all shared with your whole household.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link
                href={route('register')}
                className="flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors"
              >
                Get Started Free
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#pricing"
                className="px-6 py-3 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
              >
                See pricing
              </a>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden bg-slate-50 dark:bg-slate-900">
            <img
              src="/marketing/dashboard.png"
              alt="Freeezer dashboard showing freezer inventory, recent recipes, and meal plan"
              className="w-full h-auto"
            />
          </div>
        </section>

        {/* Core loop */}
        <section className="bg-slate-50 dark:bg-slate-900/50 py-20">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white text-center mb-3">
              How it works
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-center mb-12 max-w-xl mx-auto">
              The whole point of Freeezer, in four steps.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {LOOP_STEPS.map(({ icon: Icon, title, body }, i) => (
                <div key={title} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-9 h-9 rounded-lg bg-cyan-50 dark:bg-cyan-900/30 flex items-center justify-center shrink-0">
                      <Icon className="w-4.5 h-4.5 text-cyan-600 dark:text-cyan-400" />
                    </div>
                    <span className="text-xs font-semibold text-slate-400 dark:text-slate-500">
                      Step {i + 1}
                    </span>
                  </div>
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-white mb-1.5">
                    {title}
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                    {body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature grid */}
        <section className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white text-center mb-3">
            Everything you need to run your kitchen
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-center mb-12 max-w-xl mx-auto">
            Built for households that cook from their freezer.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-xl border border-slate-200 dark:border-slate-800 p-6">
                <div className="w-9 h-9 rounded-lg bg-cyan-50 dark:bg-cyan-900/30 flex items-center justify-center mb-4">
                  <Icon className="w-4.5 h-4.5 text-cyan-600 dark:text-cyan-400" />
                </div>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-white mb-1.5">
                  {title}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Recipe showcase */}
        <section className="bg-slate-50 dark:bg-slate-900/50 py-20">
          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden order-2 lg:order-1">
              <img
                src="/marketing/recipe.png"
                alt="A recipe detail page with a photo, ingredients, and directions"
                className="w-full h-auto"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-4">
                Your recipes, made effortless
              </h2>
              <p className="text-slate-500 dark:text-slate-400 leading-relaxed mb-4">
                Paste a URL to import any recipe, or add your own from scratch — either way, the
                photo, ingredients, and directions all live in one place. Scale servings and
                amounts update automatically.
              </p>
              <p className="text-slate-500 dark:text-slate-400 leading-relaxed">
                Then log every cook, add notes on what worked, and build a history you can look
                back on.
              </p>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="max-w-6xl mx-auto px-6 py-20 scroll-mt-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white text-center mb-3">
            Simple pricing
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-center mb-12 max-w-xl mx-auto">
            Start free. Upgrade when your household is ready for more.
          </p>
          <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-6">
              <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                Free
              </h3>
              <p className="text-3xl font-bold text-slate-900 dark:text-white mb-4">$0</p>
              <ul className="space-y-2.5 mb-6">
                {['1 freezer', 'Manual recipe entry', 'Meal planning & activity log'].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                    <Check className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                href={route('register')}
                className="block text-center px-4 py-2.5 text-sm font-medium border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors"
              >
                Get Started
              </Link>
            </div>

            <div className="rounded-xl border-2 border-cyan-500 p-6 relative">
              <span className="absolute -top-3 left-6 px-2.5 py-0.5 text-xs font-semibold bg-cyan-500 text-white rounded-full">
                Most popular
              </span>
              <h3 className="text-sm font-semibold text-cyan-600 dark:text-cyan-400 uppercase tracking-wide mb-2">
                Household
              </h3>
              <p className="text-3xl font-bold text-slate-900 dark:text-white mb-1">
                {monthlyPrice ?? '$4.99/mo'}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mb-4">
                or {annualPrice ?? '$39.99/yr'}
              </p>
              <ul className="space-y-2.5 mb-6">
                {['Unlimited freezers', 'Recipe import from any URL', 'Alexa voice control', 'Unlimited household members'].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                    <Check className="w-4 h-4 text-cyan-500 mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                href={route('register')}
                className="block text-center px-4 py-2.5 text-sm font-medium bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors"
              >
                Get Started
              </Link>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-slate-900 py-16">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Ready to stop wondering what's in your freezer?
            </h2>
            <Link
              href={route('register')}
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors"
            >
              Get Started Free
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="max-w-6xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4">
          <span className="text-xs text-slate-400 dark:text-slate-500">
            &copy; {new Date().getFullYear()} <Wordmark />
          </span>
          <div className="flex gap-4 text-xs text-slate-400 dark:text-slate-500">
            <a href="/support" className="hover:text-slate-600 dark:hover:text-slate-300 transition-colors">Support</a>
            <a href="/privacy" className="hover:text-slate-600 dark:hover:text-slate-300 transition-colors">Privacy Policy</a>
            <a href="/terms" className="hover:text-slate-600 dark:hover:text-slate-300 transition-colors">Terms of Use</a>
          </div>
        </footer>
      </div>
    </>
  )
}
